import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Trophy, TrendingUp, Star, Target, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';

const AIFeedbackCard = ({ feedback, derivedStats, onContinue }) => {
  if (!feedback) return null;

  const getGradeColor = (grade) => {
    if (!grade || grade === 'N/A') return 'bg-slate-600';
    if (grade.startsWith('A')) return 'bg-green-500';
    if (grade.startsWith('B')) return 'bg-blue-500';
    if (grade.startsWith('C')) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const getGradeEmoji = (grade) => {
    if (!grade || grade === 'N/A') return '📋';
    if (grade === 'A') return '🏆';
    if (grade === 'A-') return '⭐';
    if (grade.startsWith('B')) return '👍';
    return '💪';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 p-4">
      <div className="max-w-lg mx-auto space-y-4">
        {/* Grade Card */}
        <Card className="bg-slate-800/90 border-slate-700 overflow-hidden">
          <div className={`${getGradeColor(feedback.grade)} p-6 text-center`}>
            <div className="text-6xl mb-2">{getGradeEmoji(feedback.grade)}</div>
            <div className="text-5xl font-bold text-white mb-1">{feedback.grade}</div>
            <div className="text-white/80">Performance Grade</div>
            {feedback.provisional && feedback.grade !== 'N/A' && (
              <div
                className="inline-block mt-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/25 text-white/90"
                data-testid="provisional-grade-badge"
              >
                Provisional · small sample
              </div>
            )}
          </div>
          <CardContent className="pt-4">
            <p className="text-slate-300 text-center">{feedback.summary}</p>
          </CardContent>
        </Card>

        {/* Key Metrics */}
        {derivedStats?.total_plays > 0 && (
          <Card className="bg-slate-800/90 border-slate-700" data-testid="key-metrics-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-blue-400" />
                Key Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-700/50 rounded-lg p-3 text-center" data-testid="metric-total-plays">
                  <div className="text-2xl font-bold text-white">{derivedStats.total_plays}</div>
                  <div className="text-xs text-slate-400 mt-1">Plays Tracked</div>
                </div>
                <div className="bg-slate-700/50 rounded-lg p-3 text-center" data-testid="metric-success-rate">
                  <div className="text-2xl font-bold text-white">
                    {derivedStats.overall_success_rate ?? 0}%
                  </div>
                  <div className="text-xs text-slate-400 mt-1">Success Rate</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Skill Breakdown - only the skills actually tallied */}
        {derivedStats?.skills?.length > 0 && (
          <Card className="bg-slate-800/90 border-slate-700" data-testid="skill-breakdown-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-400" />
                Skill Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {derivedStats.skills.map((skill) => (
                <div key={skill.key} data-testid={`skill-row-${skill.key}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-slate-300 flex items-center gap-2">
                      <span>{skill.icon}</span>
                      {skill.label}
                    </span>
                    <span className="text-sm font-semibold text-white tabular-nums">
                      {skill.successes}/{skill.attempts}
                      <span className="text-slate-400 ml-2">{Math.round(skill.success_rate)}%</span>
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-slate-700 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        skill.success_rate >= 70
                          ? 'bg-green-500'
                          : skill.success_rate >= 55
                          ? 'bg-amber-500'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.max(skill.success_rate, 2)}%` }}
                    />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Kudos */}
        {feedback.kudos && feedback.kudos.length > 0 && (
          <Card className="bg-slate-800/90 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400" />
                Highlights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {feedback.kudos.map((kudo, index) => (
                <div 
                  key={`kudo-${kudo.slice(0, 20)}-${index}`} 
                  className="flex items-start gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg"
                >
                  <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Trophy className="w-3 h-3 text-green-400" />
                  </div>
                  <p className="text-green-300 text-sm">{kudo}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Growth Insights */}
        {feedback.growth_insights && feedback.growth_insights.length > 0 && (
          <Card className="bg-slate-800/90 border-slate-700">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                Areas to Improve
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {feedback.growth_insights.map((insight, index) => (
                <div 
                  key={`insight-${insight.slice(0, 20)}-${index}`} 
                  className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg"
                >
                  <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Target className="w-3 h-3 text-blue-400" />
                  </div>
                  <p className="text-blue-300 text-sm">{insight}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Continue Button */}
        <Button
          className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white py-6"
          onClick={onContinue}
          data-testid="continue-to-dashboard-btn"
        >
          Continue to Dashboard
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default AIFeedbackCard;
